<template>
  <section class="users-shell">
    <div class="users-hero">
      <div class="users-copy">
        <span class="eyebrow">Administración</span>
        <h1 class="users-title">Usuarios</h1>
        <p class="users-subtitle">Asigne el perfil “Revisor de fichas” para autorizar revisiones. Al retirarlo o desactivar al usuario, sus revisiones pendientes quedarán disponibles para reasignación.</p>
      </div>
      <button class="button users-create" @click="openCreateModal">Nuevo usuario</button>
    </div>

    <article v-if="errorMessage" class="users-alert">
      {{ errorMessage }}
    </article>

    <div class="users-panel">
      <div v-if="isLoading && !users.length" class="users-loader users-loader--inline">
        <div class="users-spinner">
          <span class="users-spinner-ring"></span>
          <span class="users-spinner-core"></span>
        </div>
        <p class="users-loader-title">Cargando usuarios</p>
        <p class="users-loader-copy">Preparando el directorio disponible.</p>
      </div>

      <template v-if="!isLoading || users.length">
        <div class="users-panel-head" :class="{ 'users-panel-head--loading': isLoading && users.length }">
          <div>
            <p class="users-panel-label">Directorio</p>
            <p class="users-panel-value">{{ users.length }} usuarios</p>
          </div>
          <div class="users-columns is-desktop-only">
            <span>Perfiles</span>
            <span>Estado</span>
            <span class="has-actions">Acciones</span>
          </div>
        </div>

        <div class="users-list" :class="{ 'users-list--loading': isLoading && users.length }">
          <article class="user-row" v-for="user in users" :key="user.id">
            <div class="user-main">
              <div class="user-identity">
                <div class="user-avatar">{{ getInitials(user.name) }}</div>
                <div>
                  <h2 class="user-name">{{ user.name }}</h2>
                  <p class="user-email">{{ user.email }}</p>
                </div>
              </div>

              <div class="user-meta">
                <div class="user-block">
                  <span class="meta-label is-mobile-only">Perfiles</span>
                  <div class="profile-list">
                    <span class="profile-pill" v-for="profile in user.profiles" :key="profile.id">
                      {{ profile.description }}
                    </span>
                  </div>
                </div>

                <div class="user-block user-status-block">
                  <span class="meta-label is-mobile-only">Estado</span>
                  <span class="status-pill" :class="user.enabled ? 'is-active' : 'is-inactive'">
                    {{ user.enabled ? 'Activo' : 'Inactivo' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="user-actions">
              <button class="button action-button" @click="openEditModal(user)">Editar</button>
              <button class="button action-button" @click="toggleEnabled(user)">
                {{ user.enabled ? 'Desactivar' : 'Activar' }}
              </button>
              <button class="button action-button action-accent" @click="openPasswordModal(user)">Clave</button>
            </div>
          </article>
        </div>
      </template>

      <div v-if="isLoading && users.length" class="users-loader users-loader--overlay">
        <div class="users-spinner users-spinner--compact">
          <span class="users-spinner-ring"></span>
          <span class="users-spinner-core"></span>
        </div>
        <p class="users-loader-title">Actualizando usuarios</p>
      </div>
    </div>

    <div class="modal users-modal" :class="{ 'is-active': showFormModal }">
      <div class="modal-background" @click="closeFormModal"></div>
      <div class="modal-card users-modal-card">
        <header class="modal-card-head users-modal-head">
          <div>
            <p class="users-modal-kicker">{{ formMode === 'create' ? 'Alta de cuenta' : 'Edición de cuenta' }}</p>
            <p class="modal-card-title">{{ formMode === 'create' ? 'Nuevo usuario' : 'Editar usuario' }}</p>
          </div>
          <button class="delete" aria-label="close" @click="closeFormModal"></button>
        </header>
        <section class="modal-card-body users-modal-body">
          <UserForm
            ref="userForm"
            :user-data="formUser"
            :profiles="profiles"
            :show-password="formMode === 'create'"
            @save="saveUser"
          />
        </section>
        <footer class="modal-card-foot users-modal-foot">
          <button class="button users-primary-action" :disabled="isSaving" @click="submitUserForm">
            {{ isSaving ? 'Guardando...' : formMode === 'create' ? 'Crear usuario' : 'Guardar cambios' }}
          </button>
          <button class="button users-secondary-action" :disabled="isSaving" @click="closeFormModal">Cancelar</button>
        </footer>
      </div>
    </div>

    <div class="modal users-modal" :class="{ 'is-active': showPasswordModal }">
      <div class="modal-background" @click="closePasswordModal"></div>
      <div class="modal-card users-modal-card password-modal">
        <header class="modal-card-head users-modal-head">
          <div>
            <p class="users-modal-kicker">Seguridad</p>
            <p class="modal-card-title">Cambiar contraseña</p>
          </div>
          <button class="delete" aria-label="close" @click="closePasswordModal"></button>
        </header>
        <section class="modal-card-body users-modal-body">
          <p class="password-copy">Nueva contraseña para <strong>{{ passwordTarget.name }}</strong>.</p>
          <div class="field">
            <label class="label" for="password-admin">Contraseña nueva</label>
            <div class="control">
              <input id="password-admin" v-model="newPassword" class="input users-input" type="password" placeholder="Contraseña nueva" />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot users-modal-foot">
          <button class="button users-warning-action" :disabled="isSavingPassword" @click="savePassword">
            {{ isSavingPassword ? 'Guardando...' : 'Actualizar clave' }}
          </button>
          <button class="button users-secondary-action" :disabled="isSavingPassword" @click="closePasswordModal">Cancelar</button>
        </footer>
      </div>
    </div>
  </section>
</template>

<script>
import UserForm from '@/components/users/UserForm.vue'

export default {
  name: 'UsersView',
  components: {
    UserForm
  },
  data: () => ({
    users: [],
    profiles: [],
    showFormModal: false,
    showPasswordModal: false,
    formMode: 'create',
    formUser: {
      id: null,
      name: '',
      email: '',
      enabled: true,
      profile_ids: []
    },
    passwordTarget: {
      id: 0,
      name: ''
    },
    newPassword: '',
    errorMessage: '',
    isSaving: false,
    isSavingPassword: false,
    isLoading: false
  }),
  methods: {
    async loadData () {
      this.errorMessage = ''
      this.isLoading = true
      try {
        const [usersResponse, profilesResponse] = await Promise.all([
          this.$axios.get('user/getUsers'),
          this.$axios.get('user/getProfiles')
        ])
        this.users = usersResponse.data.users
        this.profiles = profilesResponse.data.profiles
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar los usuarios.')
      } finally {
        this.isLoading = false
      }
    },
    openCreateModal () {
      this.formMode = 'create'
      this.formUser = {
        id: null,
        name: '',
        email: '',
        enabled: true,
        profile_ids: []
      }
      this.errorMessage = ''
      this.showFormModal = true
    },
    openEditModal (user) {
      this.formMode = 'edit'
      this.formUser = {
        id: user.id,
        name: user.name,
        email: user.email,
        enabled: user.enabled,
        profile_ids: user.profiles.map(profile => profile.id)
      }
      this.errorMessage = ''
      this.showFormModal = true
    },
    closeFormModal () {
      this.showFormModal = false
      this.isSaving = false
    },
    submitUserForm () {
      this.$refs.userForm.submit()
    },
    async saveUser (userPayload) {
      this.isSaving = true
      this.errorMessage = ''
      const endpoint = this.formMode === 'create' ? 'user/create' : 'user/update'
      try {
        await this.$axios.post(endpoint, { user: userPayload })
        await this.loadData()
        this.closeFormModal()
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error, 'No fue posible guardar el usuario.')
      } finally {
        this.isSaving = false
      }
    },
    async toggleEnabled (user) {
      this.errorMessage = ''
      try {
        await this.$axios.post('user/update', {
          user: {
            id: user.id,
            name: user.name,
            email: user.email,
            enabled: !user.enabled,
            profile_ids: user.profiles.map(profile => profile.id)
          }
        })
        await this.loadData()
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error, 'No fue posible actualizar el estado del usuario.')
      }
    },
    openPasswordModal (user) {
      this.passwordTarget = {
        id: user.id,
        name: user.name
      }
      this.newPassword = ''
      this.errorMessage = ''
      this.showPasswordModal = true
    },
    closePasswordModal () {
      this.showPasswordModal = false
      this.isSavingPassword = false
      this.newPassword = ''
    },
    async savePassword () {
      if (!this.newPassword) {
        this.errorMessage = 'Debe ingresar una contraseña nueva.'
        return
      }
      this.isSavingPassword = true
      this.errorMessage = ''
      try {
        await this.$axios.post('user/changePasswordAdmin', {
          user: {
            id: this.passwordTarget.id,
            password: this.newPassword
          }
        })
        this.closePasswordModal()
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error, 'No fue posible actualizar la contraseña.')
      } finally {
        this.isSavingPassword = false
      }
    },
    getInitials (name) {
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('') || 'U'
    },
    getErrorMessage (error, fallbackMessage) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message
      }
      return fallbackMessage
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>

<style scoped>
.users-shell {
  --ink: #1f2937;
  --muted: #6b7280;
  --line: rgba(15, 23, 42, 0.07);
  --shadow: 0 24px 70px rgba(15, 23, 42, 0.08);
  --accent-soft: #f3efe7;
  --success: #2f855a;
  --success-soft: #e6f5ec;
  --inactive: #6b7280;
  --inactive-soft: #eef0f3;
  max-width: 1240px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 3rem;
  color: var(--ink);
}

.users-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  display: inline-block;
  margin-bottom: 0.85rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--muted);
}

.users-title {
  margin: 0;
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  line-height: 0.95;
  font-weight: 700;
  letter-spacing: -0.045em;
}

.users-subtitle {
  max-width: 38rem;
  margin: 0.8rem 0 0;
  color: var(--muted);
  font-size: 1rem;
}

.users-create {
  height: 3rem;
  padding: 0 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: #fff;
  box-shadow: 0 12px 28px rgba(31, 41, 55, 0.14);
}

.users-create:hover {
  color: #fff;
}

.users-alert {
  margin-bottom: 1rem;
  padding: 0.95rem 1rem;
  border: 1px solid rgba(127, 29, 29, 0.08);
  border-radius: 18px;
  background: #fff1f2;
  color: #9f1239;
}

.users-panel {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(250, 250, 249, 0.9));
  box-shadow: var(--shadow);
  backdrop-filter: blur(16px);
}

.users-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
}

.users-loader--inline {
  min-height: 220px;
  padding: 2rem 1rem 1.5rem;
}

.users-loader--overlay {
  position: absolute;
  inset: 7.5rem 0 auto;
  z-index: 2;
}

.users-loader-title {
  margin: 0;
  color: #162033;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.users-loader-copy {
  max-width: 20rem;
  margin: 0;
  color: rgba(36, 31, 25, 0.56);
  font-size: 0.88rem;
}

.users-spinner {
  position: relative;
  width: 56px;
  height: 56px;
}

.users-spinner--compact {
  width: 42px;
  height: 42px;
}

.users-spinner-ring,
.users-spinner-core {
  position: absolute;
  inset: 0;
  border-radius: 50%;
}

.users-spinner-ring {
  border: 1.5px solid rgba(36, 31, 25, 0.08);
  border-top-color: #33281d;
  border-right-color: rgba(74, 60, 43, 0.35);
  animation: users-spin 0.9s cubic-bezier(0.65, 0.05, 0.36, 1) infinite;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.users-spinner-core {
  inset: 10px;
  background: radial-gradient(circle at 30% 30%, #ffffff, #ece2d5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

@keyframes users-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.users-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  padding: 1.35rem 1.5rem 0.25rem;
  transition: opacity 0.2s ease, filter 0.2s ease;
}

.users-panel-head--loading {
  opacity: 0.35;
  filter: saturate(0.75);
}

.users-panel-label,
.users-columns span,
.meta-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
}

.users-panel-label,
.meta-label {
  margin: 0;
}

.users-panel-value {
  margin: 0.3rem 0 0;
  font-size: 1.08rem;
  font-weight: 600;
}

.users-columns {
  min-width: 44%;
  display: grid;
  grid-template-columns: 1.7fr 0.8fr 1fr;
  gap: 1rem;
}

.users-columns .has-actions {
  text-align: right;
}

.users-list {
  padding: 0.25rem 1rem 0.9rem;
  transition: opacity 0.2s ease, filter 0.2s ease;
}

.users-list--loading {
  opacity: 0.35;
  filter: saturate(0.75);
  pointer-events: none;
}

.user-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.95rem 1rem;
  border-top: 1px solid var(--line);
}

.user-row:first-child {
  border-top: 0;
}

.user-main {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(250px, 290px) 1fr;
  gap: 1rem 1.25rem;
  align-items: center;
}

.user-identity {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.65rem;
  height: 2.65rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #efede8, #e8edf3);
  color: #111827;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.user-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 650;
}

.user-email {
  margin: 0.18rem 0 0;
  color: #7b8492;
  font-size: 0.9rem;
}

.user-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.9rem;
}

.user-block {
  min-width: 0;
}

.user-status-block {
  min-width: 5.5rem;
  text-align: left;
}

.meta-label {
  display: none;
  margin-bottom: 0.45rem;
}

.profile-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.profile-pill,
.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 1.85rem;
  padding: 0.28rem 0.68rem;
  border-radius: 999px;
  font-size: 0.79rem;
  font-weight: 600;
}

.profile-pill {
  background: var(--accent-soft);
  color: #596273;
}

.status-pill.is-active {
  background: var(--success-soft);
  color: var(--success);
}

.status-pill.is-inactive {
  background: var(--inactive-soft);
  color: var(--inactive);
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.action-button {
  min-width: 6.2rem;
  height: 2.35rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  color: var(--ink);
  box-shadow: none;
}

.action-accent {
  background: #efe6ce;
  border-color: rgba(180, 130, 20, 0.12);
}

.users-modal-card {
  width: min(760px, calc(100vw - 2rem));
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.22);
}

.users-modal {
  z-index: 700;
}

.users-modal-head,
.users-modal-foot {
  border: 0;
  background: #fcfcfb;
}

.users-modal-head {
  padding: 1.4rem 1.5rem 1rem;
}

.users-modal-body {
  background: #fcfcfb;
  padding: 0.5rem 1.5rem 1.5rem;
}

.users-modal-foot {
  padding: 0 1.5rem 1.5rem;
}

.users-modal-kicker {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
}

.password-copy {
  margin: 0 0 1rem;
  color: var(--muted);
}

.users-primary-action,
.users-secondary-action,
.users-warning-action {
  height: 2.9rem;
  padding: 0 1rem;
  border-radius: 999px;
}

.users-primary-action {
  border: 0;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: #fff;
}

.users-primary-action:hover {
  color: #fff;
}

.users-secondary-action {
  border: 1px solid var(--line);
  background: #fff;
}

.users-warning-action {
  border: 0;
  background: #ead59c;
  color: #543d04;
}

.users-input {
  border-radius: 16px;
}

.is-mobile-only {
  display: none;
}

@media screen and (max-width: 1080px) {
  .users-columns {
    display: none;
  }

  .is-desktop-only {
    display: none;
  }

  .is-mobile-only {
    display: block;
  }

  .users-hero,
  .user-row,
  .user-meta {
    flex-direction: column;
    align-items: stretch;
  }

  .user-main {
    grid-template-columns: 1fr;
  }

  .meta-label {
    display: block;
  }

  .user-actions {
    flex-wrap: wrap;
  }
}

@media screen and (max-width: 640px) {
  .users-shell {
    padding: 1.5rem 0.85rem 2rem;
  }

  .users-panel-head,
  .users-list {
    padding-left: 0.85rem;
    padding-right: 0.85rem;
  }

  .users-loader--overlay {
    inset: 6rem 0 auto;
  }

  .user-row {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
  }

  .action-button {
    min-width: 0;
    flex: 1 1 auto;
  }
}
</style>

